import { makeAutoObservable, reaction, type IReactionDisposer, runInAction } from 'mobx'
import { showToast } from 'shared/ui'
import { RequestLoadData } from 'shared/api/lib'
import {
  type IIntegrationChannel,
  type IParamsPagination,
  type Integration,
  IntegrationsApi,
} from 'entities/Integrations'

const defaultPagination = {
  page: 1,
  limit: 10,
}

export class IntegrationHubspotSettingsChannelsStore {
  private _disposeChangePagination: Null<IReactionDisposer> = null
  private _disposeSearch: Null<IReactionDisposer> = null
  private _total = 0
  private _paginationData = defaultPagination
  private _search = ''
  private _channels = new Map<number, IIntegrationChannel>()

  constructor(private _integration: Integration) {
    makeAutoObservable(this)
  }

  init() {
    this.getData(this._paginationData)
    this._reactionChange()
  }

  private _reactionChange = () => {
    this._disposeChangePagination?.()
    this._disposeSearch?.()
    this._disposeChangePagination = reaction(
      () => this._paginationData,
      () => {
        this.getData({
          ...this._paginationData,
          search: this._search,
        })
      }
    )
    this._disposeSearch = reaction(
      () => this.search,
      () => {
        this._requestLoadData.debounceLoadData({
          page: 1,
          limit: this._paginationData.limit,
          search: this._search,
        })
      }
    )
  }

  dispose = () => {
    this._paginationData = defaultPagination
    this._total = 0
    this._disposeChangePagination?.()
    this._disposeSearch?.()
    this._requestLoadData.dispose()
  }

  private _requestLoadData = new RequestLoadData<IParamsPagination>({
    requestCallback: async ({ cancelRequest }, params) => {
      if (!params) return

      if (!this.search) {
        delete params.search
      }

      const { data } = await IntegrationsApi.getIntegrationChannels(this._integration.key, params, {
        cancelToken: cancelRequest.token,
      })

      runInAction(() => {
        this._channels.clear()
        this._total = data.total
        data.data.forEach((chanel) => {
          this._channels.set(chanel.id, chanel)
        })
      })
    },
  })

  getData = (params: IParamsPagination) => {
    return this._requestLoadData.loadData(params)
  }

  refreshData = async () => {
    await this.getData({
      ...this._paginationData,
      search: this._search,
    })
    showToast({
      type: 'success',
      title: 'Custom channels refreshed',
    })
  }

  onChangePagination = (page: number, limit: number) => {
    this._paginationData = {
      page,
      limit,
    }
  }

  onSearchChange = (search: string) => {
    this._search = search
  }

  get search() {
    return this._search
  }

  get channels() {
    return Array.from(this._channels.values())
  }

  get status() {
    return this._requestLoadData.status
  }

  get total() {
    return this._total
  }

  get pagination() {
    return this._paginationData
  }
}
